import React, { useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Close } from '@emotion-icons/material/Close'
import { IconButton, Typography } from '@mui/material'
import { Colors, Shapes } from 'src/components/theme'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'
import { AMParcelOverview } from 'src/common-services'
import { ResultItem, SidebarResultListItem } from 'src/v2/components/sidebar-result-list'
import { formatString } from 'src/utils/format-string'
import { EmptyResultItem } from 'src/views/map-explorer/sidebar/panels/results/empty-result-item'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { SidebarResultOverlay } from 'src/v2/components/sidebar-result-overlay'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { useAtomValueLoadable } from 'src/utils/jotai'
import { MapSquareIcon } from 'src/components/icons/map-square'

import * as atoms from 'src/v2/state/owner-reveal/entity-parcels'
import { SidebarResultsSkeletonLoaderItem } from 'src/views/map-explorer/sidebar/panels/loader'

const buildOwnerParcels = (parcels: AMParcelOverview[]): SidebarResultListItem[] => {
  return parcels.map((parcel) => ({
    id: parcel.id,
    title: formatString(parcel.address || '', 'address'),
    subtitle: `APN # ${parcel.apn || ''}`,
    color: Colors.primaryGreen700,
    icon: <MapSquareIcon size={14} />,
  }))
}

const ResultsRow = styled(FlexRow)`
  flex: 1;
  gap: ${Shapes.spacing[2]}px;
  flex-wrap: wrap;
`

type OwnerParcelsContentProps = {
  open: boolean
  onClose: () => void
  parentRef: React.RefObject<HTMLDivElement>
  parcels: AMParcelOverview[]
}
const OwnerParcelsContent: React.FC<OwnerParcelsContentProps> = ({ open, onClose, parentRef, parcels }) => {
  const { selectParcel } = useMapExplorerNavigation()

  const results = useMemo(() => {
    if (!parcels) {
      return undefined
    }
    return buildOwnerParcels(parcels)
  }, [parcels])

  if (!results) {
    return <EmptyResultItem title="Owner" />
  }

  return (
    <>
      <FlexColumn gap={Shapes.spacing[1]}>
        {results.slice(0, 3).map((owner) => (
          <ResultItem key={owner.id} item={owner} onClick={() => selectParcel(owner.id)} mini maxWidth={250} />
        ))}
      </FlexColumn>
      <SidebarResultOverlay
        open={open}
        anchorEl={parentRef.current}
        onClose={onClose}
        maxWidth={650}
        contentPadding={Shapes.spacing[2]}>
        <FlexColumn flex="1" gap={Shapes.spacing[1]}>
          <FlexRow justify="space-between" align="center">
            <Typography variant="textmd" color={Colors.neutral700}>
              Parcels ({results.length})
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <Close size={16} />
            </IconButton>
          </FlexRow>
          <ResultsRow>
            {results.map((parcel) => (
              <ResultItem key={parcel.id} item={parcel} onClick={() => selectParcel(parcel.id)} mini />
            ))}
          </ResultsRow>
        </FlexColumn>
      </SidebarResultOverlay>
    </>
  )
}

const OwnerParcelsWrapper = styled(FlexColumn)`
  flex: 0;
  gap: ${Shapes.spacing[4]}px;
  padding: ${Shapes.spacing[5]}px ${Shapes.spacing[4]}px;
`

export const OwnerParcels: React.FC = () => {
  const parcels = useAtomValueLoadable(atoms.selectedEntityParcelsOverview)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const onToggleOpen = () => {
    setOpen((prev) => !prev)
  }
  const onClose = () => {
    setOpen(false)
  }
  return (
    <OwnerParcelsWrapper ref={ref}>
      <FlexRow justify="space-between" align="center">
        <Typography variant="title2" color={Colors.neutral950}>
          Owner Parcels {parcels.state === 'hasData' && parcels.data?.length ? `(${parcels.data.length})` : ''}
        </Typography>
        {parcels.state === 'hasData' && parcels.data?.length && parcels.data.length > 3 && (
          <ButtonWithIcon onClick={onToggleOpen}>View {open ? 'Less' : 'All'}</ButtonWithIcon>
        )}
      </FlexRow>
      {parcels.state === 'loading' ? (
        <FlexColumn gap={Shapes.spacing[1]}>
          <SidebarResultsSkeletonLoaderItem />
          <SidebarResultsSkeletonLoaderItem />
        </FlexColumn>
      ) : parcels.state === 'hasData' && parcels.data ? (
        <OwnerParcelsContent parentRef={ref} open={open} onClose={onClose} parcels={parcels.data} />
      ) : null}
    </OwnerParcelsWrapper>
  )
}
